import { X, Plus, GripVertical } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { InlineEditor } from './solutionPrimitives'
import {
  SeverityDot, ImportanceRing, SolutionMarker, LinkChip,
  severityLabel, importanceLabel,
} from './valueDesignPrimitives'
import type { LinkChipOption } from './valueDesignPrimitives'
import type { VPCPainReliever, VPCGainCreator } from '@ispot/shared'


interface RowChromeProps {
  side: 'left' | 'right'
  gridRow: number
  id: string                          // P1, R1, O1, G1
  sortableId: string                  // db id used by dnd-kit
  hovered?: boolean
  linked?: boolean
  onHover?: (id: string | null) => void
  draggable?: boolean
  children: React.ReactNode
}

function RowChrome({
  side, gridRow, id, sortableId, hovered, linked, onHover, draggable = true, children,
}: RowChromeProps) {
  const isActive = hovered || linked
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: sortableId, disabled: !draggable })

  return (
    <div
      ref={setNodeRef}
      data-id={id}
      className="grid items-center min-w-0 rounded transition-colors grid-cols-[14px_22px_14px_minmax(0,1fr)_auto] gap-x-2.5 px-2 group"
      style={{
        gridColumn: side === 'left' ? 1 : 3,
        gridRow,
        background: isActive ? 'var(--color-accent-50)' : undefined,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
        zIndex: isDragging ? 10 : undefined,
      }}
      onMouseEnter={() => onHover?.(id)}
      onMouseLeave={() => onHover?.(null)}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="opacity-0 group-hover:opacity-50 hover:opacity-100 cursor-grab active:cursor-grabbing text-text-muted transition-opacity"
        aria-label={`Drag ${id} to reorder`}
        tabIndex={-1}
      >
        <GripVertical className="w-3 h-3" />
      </button>
      {children}
    </div>
  )
}

function Idx({ label, dimmed }: { label: string; dimmed?: boolean }) {
  return (
    <span
      className="font-mono text-[10.5px] text-text-muted text-right"
      style={{ opacity: dimmed ? 0.4 : 1 }}
    >
      {label}
    </span>
  )
}


interface PainRowProps {
  gridRow: number
  id: string
  sortableId: string
  pain: { id: string; description: string; severity?: string }
  linkIds: string[]
  options: LinkChipOption[]
  onToggleLink: (relieverIdx: number) => void
  hovered?: boolean
  linked?: boolean
  onHover?: (id: string | null) => void
}

export function PainRow({
  gridRow, id, sortableId, pain, linkIds, options, onToggleLink, hovered, linked, onHover,
}: PainRowProps) {
  return (
    <RowChrome side="left" gridRow={gridRow} id={id} sortableId={sortableId}
               hovered={hovered} linked={linked} onHover={onHover}>
      <Idx label={id} />
      <SeverityDot severity={pain.severity} />
      <span className="text-[13.5px] text-text leading-snug overflow-hidden line-clamp-2 min-w-0">
        {pain.description}
        <em className="not-italic text-text-muted text-[11.5px] ml-1.5">{severityLabel(pain.severity)}</em>
      </span>
      <LinkChip
        ids={linkIds} direction="forward" kind="pain-reliever"
        active={hovered || linked}
        options={options} onToggle={onToggleLink}
        popoverHeading={`Relievers for ${id}`}
      />
    </RowChrome>
  )
}


interface OutcomeRowProps {
  gridRow: number
  id: string
  sortableId: string
  outcome: { id: string; description: string; importance?: string }
  linkIds: string[]
  options: LinkChipOption[]
  onToggleLink: (creatorIdx: number) => void
  hovered?: boolean
  linked?: boolean
  onHover?: (id: string | null) => void
}

export function OutcomeRow({
  gridRow, id, sortableId, outcome, linkIds, options, onToggleLink, hovered, linked, onHover,
}: OutcomeRowProps) {
  return (
    <RowChrome side="left" gridRow={gridRow} id={id} sortableId={sortableId}
               hovered={hovered} linked={linked} onHover={onHover}>
      <Idx label={id} />
      <ImportanceRing importance={outcome.importance} />
      <span className="text-[13.5px] text-text leading-snug overflow-hidden line-clamp-2 min-w-0">
        {outcome.description}
        <em className="not-italic text-text-muted text-[11.5px] ml-1.5">{importanceLabel(outcome.importance)}</em>
      </span>
      <LinkChip
        ids={linkIds} direction="forward" kind="outcome-creator"
        active={hovered || linked}
        options={options} onToggle={onToggleLink}
        popoverHeading={`Creators for ${id}`}
      />
    </RowChrome>
  )
}


interface RelieverRowProps {
  gridRow: number
  id: string
  sortableId: string
  reliever: VPCPainReliever
  index: number
  linkIds: string[]
  options: LinkChipOption[]
  onUpdate: (index: number, description: string) => void
  onRemove: (index: number) => void
  onToggleLink: (painIdx: number) => void
  hovered?: boolean
  linked?: boolean
  onHover?: (id: string | null) => void
}

export function RelieverRow({
  gridRow, id, sortableId, reliever, index, linkIds, options,
  onUpdate, onRemove, onToggleLink, hovered, linked, onHover,
}: RelieverRowProps) {
  return (
    <RowChrome side="right" gridRow={gridRow} id={id} sortableId={sortableId}
               hovered={hovered} linked={linked} onHover={onHover}>
      <Idx label={id} />
      <SolutionMarker />
      <div className="min-w-0 flex items-center gap-1">
        <div className="flex-1 min-w-0">
          <InlineEditor
            value={reliever.description}
            onChange={v => onUpdate(index, v)}
            placeholder="How does this relieve the pain?"
            multiline size="sm"
          />
        </div>
        <button
          onClick={() => onRemove(index)}
          className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-error shrink-0 transition-opacity"
          aria-label="Remove pain reliever"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <LinkChip
        ids={linkIds} direction="back" kind="pain-reliever"
        active={hovered || linked}
        options={options} onToggle={onToggleLink}
        popoverHeading={`Pains tackled by ${id}`}
      />
    </RowChrome>
  )
}


interface CreatorRowProps {
  gridRow: number
  id: string
  sortableId: string
  creator: VPCGainCreator
  index: number
  linkIds: string[]
  options: LinkChipOption[]
  onUpdate: (index: number, description: string) => void
  onRemove: (index: number) => void
  onToggleLink: (outcomeIdx: number) => void
  hovered?: boolean
  linked?: boolean
  onHover?: (id: string | null) => void
}

export function CreatorRow({
  gridRow, id, sortableId, creator, index, linkIds, options,
  onUpdate, onRemove, onToggleLink, hovered, linked, onHover,
}: CreatorRowProps) {
  return (
    <RowChrome side="right" gridRow={gridRow} id={id} sortableId={sortableId}
               hovered={hovered} linked={linked} onHover={onHover}>
      <Idx label={id} />
      <ImportanceRing importance="expected" />
      <div className="min-w-0 flex items-center gap-1">
        <div className="flex-1 min-w-0">
          <InlineEditor
            value={creator.description}
            onChange={v => onUpdate(index, v)}
            placeholder="How does this create the gain?"
            multiline size="sm"
          />
        </div>
        <button
          onClick={() => onRemove(index)}
          className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-error shrink-0 transition-opacity"
          aria-label="Remove gain creator"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <LinkChip
        ids={linkIds} direction="back" kind="outcome-creator"
        active={hovered || linked}
        options={options} onToggle={onToggleLink}
        popoverHeading={`Outcomes delivered by ${id}`}
      />
    </RowChrome>
  )
}


export function StarterRow({ gridRow, id, targetLabel, verb, onClick }: {
  side: 'right'
  gridRow: number
  id: string
  targetLabel: string
  verb: string
  onClick: () => void
}) {
  return (
    <div
      data-id={id}
      className="grid items-center grid-cols-[14px_22px_14px_minmax(0,1fr)] gap-x-2.5 px-2 min-w-0"
      style={{ gridColumn: 3, gridRow }}
    >
      <span aria-hidden />
      <Idx label={id} dimmed />
      <span aria-hidden />
      <button
        onClick={onClick}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded border border-dashed border-border-strong text-[12.5px] text-text-secondary hover:bg-accent-50 hover:border-accent-500 hover:text-accent-500 transition-colors text-left min-w-0"
      >
        <Plus className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">
          {verb} for <strong className="font-semibold text-text">{targetLabel}</strong>
        </span>
      </button>
    </div>
  )
}

export function EmptyRow({ side, gridRow, id }: { side: 'left' | 'right'; gridRow: number; id: string }) {
  return (
    <div
      data-id={id}
      className="grid items-center grid-cols-[14px_22px_14px_minmax(0,1fr)] gap-x-2.5 px-2"
      style={{ gridColumn: side === 'left' ? 1 : 3, gridRow }}
    >
      <span aria-hidden />
      <Idx label={id} dimmed />
      <span aria-hidden />
      <span className="text-[12px] text-text-muted italic opacity-50">—</span>
    </div>
  )
}
